import { ReactNode, useEffect, useState, useRef, lazy, Suspense } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { supabase } from "@/integrations/supabase/client";
import { EssayChatProvider, useEssayChatContext } from "@/contexts/EssayChatContext";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { PanelRightOpen, Loader2 } from "lucide-react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { useLocation } from "react-router-dom";

const EssayChatPanel = lazy(() => import("@/components/EssayChatPanel").then(m => ({ default: m.EssayChatPanel })));

interface AppLayoutProps {
  children: ReactNode;
}

const getDefaultSidebarOpen = () => {
  if (typeof window === 'undefined') return true;
  return window.innerWidth >= 768;
};

function AuthenticatedLayout({ children }: { children: ReactNode }) {
  const [defaultOpen] = useState(getDefaultSidebarOpen);
  const { isChatOpen, setIsChatOpen } = useEssayChatContext();
  const isMobile = useIsMobile();
  const chatPanelRef = useRef<ImperativePanelHandle>(null);
  const chatHasOpened = useRef(false);
  const location = useLocation();

  // Show chat toggle only on analysis pages
  const isAnalysisPage = location.pathname.startsWith("/analysis/");

  useEffect(() => {
    if (isMobile) return;
    if (isChatOpen) {
      chatHasOpened.current = true;
      chatPanelRef.current?.resize(50);
    } else {
      chatPanelRef.current?.resize(0);
    }
  }, [isChatOpen, isMobile]);

  const chatPanel = chatHasOpened.current || isChatOpen ? (
    <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin" /></div>}>
      <EssayChatPanel onClose={() => setIsChatOpen(false)} />
    </Suspense>
  ) : null;

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <main className="flex-1 transition-all duration-300 relative">
          {/* Mobile header */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border flex items-center px-4 gap-3">
            <SidebarTrigger className="h-11 w-11 min-h-[44px] min-w-[44px]" />
            <span className="font-semibold text-foreground">AnnotateAI</span>
          </div>
          {/* Vertical Chat tab on right edge */}
          {isAnalysisPage && (
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className="fixed right-0 top-1/2 -translate-y-1/2 z-50 bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-4 rounded-l-xl shadow-lg transition-all duration-200 cursor-pointer"
              style={{ writingMode: "vertical-rl" }}
              aria-label="Toggle Essay Chat"
            >
              <span className="text-xs font-semibold tracking-wider">Chat</span>
            </button>
          )}
          <div className="md:pt-0 pt-14 h-screen overflow-hidden">
            {isMobile ? (
              <>
                <div className="h-full overflow-y-auto">{children}</div>
                <Drawer open={isChatOpen} onOpenChange={setIsChatOpen}>
                  <DrawerContent className="h-[85vh]">
                    {chatPanel}
                  </DrawerContent>
                </Drawer>
              </>
            ) : (
              <ResizablePanelGroup direction="horizontal" className="h-full">
                <ResizablePanel defaultSize={100} minSize={30} className="overflow-hidden">
                  <div className="h-full overflow-y-auto">{children}</div>
                </ResizablePanel>
                {isChatOpen && <ResizableHandle withHandle />}
                <ResizablePanel
                  ref={chatPanelRef}
                  defaultSize={0}
                  minSize={0}
                  className="overflow-hidden"
                  onResize={(size) => {
                    if (size === 0 && isChatOpen) setIsChatOpen(false);
                  }}
                >
                  {chatPanel}
                </ResizablePanel>
              </ResizablePanelGroup>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session?.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (isAuthenticated === null) return <>{children}</>;
  if (!isAuthenticated) return <>{children}</>;

  return (
    <EssayChatProvider>
      <AuthenticatedLayout>{children}</AuthenticatedLayout>
    </EssayChatProvider>
  );
}
